import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  FileText, ArrowLeft, User, Mail, Crown, 
  Calendar, BarChart3, LogOut, Shield, AlertTriangle, Trash2
} from 'lucide-react';
import MainMenu from '@/components/MainMenu';

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [usage, setUsage] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const effectivePlan = user?.effective_plan || user?.plan || 'free';
  const betaAccess = user?.beta_access;
  const isBetaAccessActive = Boolean(betaAccess?.active);
  const formatPlanName = (plan) => `${plan?.charAt(0).toUpperCase() || ''}${plan?.slice(1) || ''}`;
  const formatDateLabel = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString();
  };
  const scheduledEndDateLabel = formatDateLabel(user?.current_period_end);
  const hasScheduledCancellation = Boolean(user?.cancel_at_period_end);

  useEffect(() => {
    loadUsage();
  }, []);

  const loadUsage = async () => {
    try {
      const data = await api.getUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to load usage:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const data = await api.cancelSubscription();
      toast.success(
        data?.current_period_end
          ? `Subscription cancellation scheduled. Access remains active until ${formatDateLabel(data.current_period_end)}.`
          : (data?.message || 'Subscription cancellation scheduled')
      );
      await refreshUser();
      setShowCancelConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      toast.error('Enter your current password to delete your account');
      return;
    }

    setDeleting(true);
    try {
      await api.deleteAccount(deletePassword);
      toast.success('Account deleted successfully');
      setDeletePassword('');
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const getPlanBadge = () => {
    if (user?.is_admin) {
      return (
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-amber-400 to-orange-500 text-white">
          Admin
        </span>
      );
    }
    const badges = {
      free: { color: 'bg-slate-100 text-slate-600', label: 'Free Plan' },
      pro: { color: 'bg-primary/10 text-primary', label: 'Pro Plan' },
      premium: { color: 'bg-gradient-to-r from-amber-400 to-orange-500 text-white', label: 'Premium Plan' }
    };
    const badge = badges[effectivePlan] || badges.free;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {isBetaAccessActive ? `Beta ${badge.label}` : badge.label}
      </span>
    );
  };

  const usageRows = [
    { key: 'analyses', label: 'Resume analyses' },
    { key: 'comparisons', label: 'Resume comparisons' },
    { key: 'resume_generations', label: 'Custom resume generations' }
  ];

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <MainMenu />
          <button
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold">Account Settings</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Profile Card */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="profile-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <User className="w-8 h-8 text-primary" />
                )}
              </div>
              <div>
                <h2 className="font-heading text-xl font-semibold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{user?.email}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>Member since {new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Subscription Card */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="subscription-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Subscription</h3>
              </div>
              {getPlanBadge()}
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-surface rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Current Access</span>
                  <span className="font-medium capitalize">
                    {isBetaAccessActive ? `${formatPlanName(effectivePlan)} beta access` : formatPlanName(effectivePlan)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Billing Plan</span>
                  <span className="font-medium capitalize">{formatPlanName(user?.plan || 'free')}</span>
                </div>
                {hasScheduledCancellation && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <p className="font-medium">Cancellation scheduled</p>
                    <p className="mt-1">
                      No further renewals. Your {formatPlanName(user?.plan || effectivePlan)} access remains active until{' '}
                      {scheduledEndDateLabel || 'the end of your current billing period'}.
                    </p>
                  </div>
                )}
                {betaAccess && (
                  <div className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground space-y-1">
                    <p>
                      Beta status: <span className="font-medium text-foreground capitalize">{betaAccess.status}</span>
                    </p>
                    {betaAccess.expires_at && (
                      <p>
                        Beta access expires on{' '}
                        <span className="font-medium text-foreground">
                          {new Date(betaAccess.expires_at).toLocaleDateString()}
                        </span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {user?.plan === 'free' && effectivePlan === 'free' && !user?.is_admin && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary-hover transition-all"
                  data-testid="upgrade-btn"
                >
                  Upgrade to Pro
                </button>
              )}
              {user?.plan === 'pro' && !user?.is_admin && !hasScheduledCancellation && (
                <>
                  {effectivePlan !== 'premium' && (
                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-white py-3 rounded-lg font-medium hover:opacity-90 transition-all"
                      data-testid="upgrade-premium-btn"
                    >
                      Upgrade to Premium
                    </button>
                  )}
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className={`w-full text-destructive hover:underline text-sm ${effectivePlan !== 'premium' ? 'mt-2' : ''}`}
                    data-testid="cancel-sub-btn"
                  >
                    Cancel Subscription
                  </button>
                </>
              )}
              {user?.plan === 'premium' && !user?.is_admin && !hasScheduledCancellation && (
                <button
                  onClick={() => setShowCancelConfirm(true)}
                  className="w-full text-destructive hover:underline text-sm"
                  data-testid="cancel-sub-btn"
                >
                  Cancel Subscription
                </button>
              )}
              {user?.is_admin && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Shield className="w-4 h-4" />
                  Admin accounts have full access to all features
                </div>
              )}
            </div>
          </div>

          {/* Usage Stats */}
          {usage && (
            <div className="bg-white border border-border rounded-xl p-6 shadow-soft" data-testid="usage-card">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-semibold">Usage This Month</h3>
              </div>
              
              <div className="space-y-3">
                {usageRows.map((row) => {
                  const item = usage.usage?.[row.key];
                  if (!item) return null;
                  return (
                    <div key={row.key} className="flex items-center justify-between p-3 bg-surface rounded-lg">
                      <span className="text-sm text-muted-foreground">{row.label}</span>
                      <span className="font-medium">
                        {item.is_unlimited ? 'Unlimited' : `${item.used} / ${item.limit}`}
                      </span>
                    </div>
                  );
                })}
                {usage.reset_date && (
                  <p className="text-xs text-muted-foreground">
                    Monthly allowances reset on {new Date(usage.reset_date).toLocaleDateString()}.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-white border border-border py-3 rounded-xl font-medium hover:bg-surface transition-all text-destructive"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>

          {/* Privacy Policy Link */}
          <Link 
            to="/privacy" 
            className="w-full flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm"
            data-testid="privacy-link"
          >
            <Shield className="w-4 h-4" />
            Privacy Policy
          </Link>

          {/* Delete Account */}
          <div className="pt-6 border-t border-border">
            <h3 className="font-heading text-lg font-semibold text-destructive mb-2">Danger Zone</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <button
              onClick={() => {
                setDeletePassword('');
                setShowDeleteConfirm(true);
              }}
              className="flex items-center gap-2 text-destructive hover:underline text-sm"
              data-testid="delete-account-btn"
            >
              <Trash2 className="w-4 h-4" />
              Delete my account
            </button>
          </div>

          {/* Cancel Confirmation Modal */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold">Cancel Subscription?</h3>
                    <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  Your subscription will remain active until{' '}
                  {scheduledEndDateLabel || 'the end of your current billing period'}, and Stripe will not renew it after that date.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 py-2 border border-border rounded-lg hover:bg-surface transition-colors"
                    data-testid="cancel-dismiss"
                  >
                    Keep Subscription
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="flex-1 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50"
                    data-testid="cancel-confirm"
                  >
                    {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Account Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold">Delete Account?</h3>
                    <p className="text-sm text-muted-foreground">This is permanent</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  All your data including resumes, analysis history, and account information will be permanently deleted. 
                  This action cannot be undone.
                </p>
                <div className="mb-6">
                  <label htmlFor="delete-account-password" className="block text-sm font-medium text-foreground mb-2">
                    Current password
                  </label>
                  <input
                    id="delete-account-password"
                    type="password"
                    value={deletePassword}
                    onChange={(event) => setDeletePassword(event.target.value)}
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    placeholder="Enter your current password"
                    data-testid="delete-password-input"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletePassword('');
                    }}
                    className="flex-1 py-2 border border-border rounded-lg hover:bg-surface transition-colors"
                    data-testid="delete-dismiss"
                  >
                    Keep Account
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                    className="flex-1 py-2 bg-destructive text-white rounded-lg hover:bg-destructive/90 transition-colors disabled:opacity-50"
                    data-testid="delete-confirm"
                  >
                    {deleting ? 'Deleting...' : 'Delete Forever'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
